import { useAtom, useAtomValue } from 'jotai';
import { backdoLaunchAtom, clientAtom, hasTurnAtom, selectionAtom, teamsAtom, tilesAtom, turnAtom } from '../GlobalState';
import layout from '../dictionaries/layout';
import Piece from './Piece';
import { pieceSelected } from '../logicHelpers/helpers';
import MeshColors from './MeshColors';
import Star from '../meshes/Stars/Star';
import { firstAvailableTokenId } from '../gameLogic/helpers';
import { hasValidMoveBoard, hasValidMoveHome, tileType } from '../gameLogic/rules';
import PointerCommand from '../meshes/PointerCommand';
import { useGameHelpers } from '../stores/useGameHelpers';
import { useAnimationPlaying } from '../hooks/useAnimationPlaying';
import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';

export default function PiecesSection({ 
  position=[0,0,0], 
  rotation=[0,0,0], 
  scale=1,
  device
}) {
  const [client] = useAtom(clientAtom)
  const [teams] = useAtom(teamsAtom)
  const [selection] = useAtom(selectionAtom)
  const backdoLaunch = useAtomValue(backdoLaunchAtom)
  const hasTurn = useAtomValue(hasTurnAtom)

  function UnassignedPieces() {
    const emptyPieces = [0, 0, 0, 0]
    return <group>
      {emptyPieces.map((_value, index) =>
        (<mesh
          position={layout[device].game.piecesSection.emptyPieces.positions[index]}
          key={index}
        >
          <sphereGeometry args={[0.3, 32, 16]} />
          <meshStandardMaterial color="#505050"/>
        </mesh>
      ))}
    </group>
  }

  function AssignedPieces() {  
    const team = client.team  

    // Need to accept "key" to use it in an map
    function EmptyPiece({ position }) {
      return <mesh
        position={position}
      >
        <Star color='grey' scale={0.3}/>
      </mesh>
    }

    function ScoredPiece({ position }) {
      return <mesh
        position={position}
      >
        <Star color={team == 0 ? "red" : "green"} scale={0.3}/>
      </mesh>
    }



    // function Pointer({ token, tokenIndex }) {
    //   const animationPlaying = useAnimationPlaying()
    //   const tokenChoice = useGameHelpers(s => s.tokenChoice)
    //   const tokenChoices = useGameHelpers(s => s.tokenChoices)
    //   const selection = useAtomValue(selectionAtom)

    //   return !animationPlaying && 
    //   tokenChoices[parseInt(tokenChoice)] === -1 && 
    //   firstAvailableTokenId({ pieces: teams[team].pieces }) === tokenIndex && 
    //   selection === null && 
    //   hasTurn &&
    //   <PointerCommand
    //     position={[
    //       layout[device].game.piecesSection.pieces.positions[tokenIndex][0],
    //       layout[device].game.piecesSection.pieces.positions[tokenIndex][1] + 0.5,
    //       layout[device].game.piecesSection.pieces.positions[tokenIndex][2] -1,
    //     ]}
    //     rotation={[Math.PI/2, 0, 0]}
    //     color={MeshColors.menuGold}
    //     scale={1.3}
    //   />
    // }

    function PointerPad({ position }) {
      const animationPlaying = useAnimationPlaying()
      const tokenChoice = useGameHelpers(s => s.tokenChoice)
      const tokenChoices = useGameHelpers(s => s.tokenChoices)
      const selection = useAtomValue(selectionAtom)

      const pad = useRef()

      useFrame((state, delta) => {
        if (pad.current) {
          pad.current.material.opacity = 0.4 + 0.1 * Math.sin(state.clock.elapsedTime * 3)
          pad.current.scale.x = 0.9 + 0.01 * Math.sin(state.clock.elapsedTime * 3)
          pad.current.scale.y = 0.01
          pad.current.scale.z = 0.9 + 0.01 * Math.sin(state.clock.elapsedTime * 3)
        }
      })

      return !animationPlaying && 
      tokenChoices[parseInt(tokenChoice)] === -1 && 
      selection === null && 
      hasTurn && <group position={position} >
        <mesh scale={[0.7, 0.01, 0.7]} ref={pad}>
          <cylinderGeometry args={[1, 1, 1, 32]}/>
          <meshStandardMaterial  color={MeshColors.menuGold} transparent opacity={0.5}/>
        </mesh>
      </group>
    }

    function Pointer({ position }) {
      const animationPlaying = useAnimationPlaying()
      const tokenChoices = useGameHelpers(s => s.tokenChoices)
      const tokenChoice = useGameHelpers(s => s.tokenChoice)
      const turn = useAtomValue(turnAtom)
  
      return !animationPlaying && 
      tokenChoices[tokenChoice] === -1 && hasValidMoveBoard(teams[turn.team].moves) && 
      hasTurn && <group position={position}>
        <PointerCommand
          position={[0, 0, 0]}
          rotation={[Math.PI/2, 0, 0]}
          color={MeshColors.yellow}
          scale={1.3}
        />
      </group>
    }

    return <group>
      { teams[team].pieces.map((value, index) =>
        tileType(value.tile) === "onBoard" ? <EmptyPiece 
          position={layout[device].game.piecesSection.pieces.positions[index]}
          key={index}
        /> : 
        tileType(value.tile) === "scored" ? <ScoredPiece
          position={layout[device].game.piecesSection.pieces.positions[index]}
          key={index}
        /> : <group key={index}>
        {/* should spin */}
        {/* match shape to command button */}
          {/* <PointerPad position={[
            layout[device].game.piecesSection.pieces.positions[index][0],
            layout[device].game.piecesSection.pieces.positions[index][1] - 1,
            layout[device].game.piecesSection.pieces.positions[index][2] - 0.3,
          ]}/> */}
          <Pointer position={[
            layout[device].game.piecesSection.pieces.positions[index][0],
            layout[device].game.piecesSection.pieces.positions[index][1] - 1,
            layout[device].game.piecesSection.pieces.positions[index][2] - 0.3,
          ]}/>
          <Piece
            position={layout[device].game.piecesSection.pieces.positions[index]}
            rotation={layout[device].game.piecesSection.pieces.rotation}
            scale={layout[device].game.piecesSection.pieces.scale}
            tile={-1}
            team={team}
            id={value.id}
            key={index}
            // on selection, no other piece should be in 'selectable' animation
            selectable={(selection === null && hasValidMoveHome(teams[team].pieces, teams[team].moves, backdoLaunch) && hasTurn)}
            onBoard={false}
            selected={pieceSelected(selection, value.id, team)}
          />
        </group>
      )}
    </group>
  } 

  return <group
    position={position}
    rotation={rotation}
    scale={scale}
  >
    { client.team === -1 && <UnassignedPieces/> }
    { (client.team === 0 || client.team === 1) && <AssignedPieces/>}
  </group>
}